/**
 * judgeme-ada.js
 *
 * Accessibility labels for the Judge.me reviews widget.
 * Replaces the inline "Judge.me ADA Fixes" block formerly in theme.liquid.
 * Deployed on both storefronts (lionbrand.com and lionbrandyarn.ca).
 *
 * TWO WIDGET GENERATIONS
 * ----------------------
 * The stores run different Judge.me widgets, distinguished by class prefix:
 *   jdgm-   legacy widget. US store: entire review widget + write-review form.
 *           CA store: write-review modal and preview badge only.
 *   jm-     current widget. CA store: the whole review list, histogram,
 *           filters and pagination. Not present on US at all.
 *
 * This file targets links by href and class, so it works on both. Audited
 * Sept 2026 with judgeme-scan.js on a product page: US ~450-550 elements, all
 * jdgm-, zero jm-. CA ~420, split 130 jdgm- (modal + badge) and 292 jm- (the
 * review widget). Counts vary per render with how many related-product cards
 * have loaded; the prefix split is the stable fact.
 *
 * WHAT IT DOES
 * ------------
 * Labels four links whose accessible names otherwise come from their own text
 * and are meaningless out of context ("terms", "privacy", "content", "Based on
 * 685 reviews") — a WCAG 2.4.4 (Link Purpose) failure. Every one that renders
 * opens in a new tab, so the labels say so: target="_blank" verified on all
 * four on US, and on the two that exist on CA.
 *
 * Coverage differs by store:
 *   US  all four apply.
 *   CA  only terms and privacy exist. The content-policy link and
 *       .jdgm-rev__prod-link are absent from the jm- widget, so those two
 *       selectors are inert there. Left in place deliberately — one shared
 *       file is easier to maintain than two, and if US migrates to the jm-
 *       widget the stores converge on this same file.
 *
 * WHY THE REST WAS REMOVED
 * ------------------------
 * The previous inline block applied eight fixes. Audited against the live
 * widget, seven made accessibility WORSE, because Judge.me had since added
 * correct ARIA of their own. Native state at time of removal:
 *
 *   .jdgm-histogram__row          plain div. Old code made it role=button +
 *                                 tabindex=0, producing a button whose only
 *                                 accessible name was its text ("87% (598)")
 *                                 and which wrapped the real, labelled button
 *                                 in .jdgm-histogram__star. Nested interactive
 *                                 controls and a duplicate tab stop.
 *   .jdgm-histogram__bar-content  decorative width bar. Old code made it
 *                                 role=button with NO tabindex — announced as
 *                                 a button, unreachable by keyboard.
 *   .jdgm-prev-badge__stars       role=button, tabindex=0, "See all reviews".
 *                                 Old code stripped the role and left the
 *                                 tabindex, leaving a focusable element with
 *                                 no role. This is the ONE removal that also
 *                                 mattered on CA — the badge exists there too.
 *   .jdgm-rev-widg__summary-stars role=img, "Average rating is 4.80 stars".
 *                                 Old code stripped role=img, dropping it to
 *                                 generic where aria-label is unreliably
 *                                 announced. The rating was effectively lost.
 *   .jdgm-rev__pic-link           "Jillian review photo 1". Old code overwrote
 *                                 it with "Link to user picture 1", losing
 *                                 reviewer attribution.
 *   .jdgm-rev__pic-img            alt="Jillian review photo". Old code added
 *                                 aria-label, overriding alt inside an
 *                                 already-labelled link.
 *
 * The other five targeted legacy classes that do not exist on CA's jm- widget,
 * so only the .jdgm-prev-badge__stars removal changed anything there.
 *
 * Do not reinstate any of them without re-auditing first. Use
 * judgeme-scan.js, with this file removed, to see the native baseline.
 *
 * KNOWN ISSUES LEFT ALONE (Judge.me's markup, reported to their support)
 * ---------------------------------------------------------------------
 *   US  .jdgm-write-rev-link has role=button, no href, no tabindex —
 *       "Write a review" is unreachable by keyboard. WCAG 2.1.1 Level A.
 *   US  .jdgm-prev-badge__stars renders inside the product card's own <a>,
 *       a button nested in a link, one extra tab stop per card.
 *   CA  jm-*__expand-button ("More") named from text only; several identical
 *       buttons per page.
 * Deliberately not patched here. Patching a vendor's accessibility markup is
 * what produced the seven regressions above.
 *
 * WHY AN OBSERVER, NOT INTERVALS
 * ------------------------------
 * The old block used two setInterval timers. The 200ms one guarded on the
 * terms link but then dereferenced the privacy and content-policy links
 * unconditionally — when those had not rendered it threw before reaching
 * clearInterval, so it threw every 200ms for the life of the page. The 2000ms
 * one never cleared and swept the DOM forever. It also only started if
 * .jdgm-rev__prod-link existed at window.load, so on slow loads the fixes
 * silently never applied at all.
 *
 * This reacts to Judge.me rendering or re-rendering (pagination, filtering,
 * sorting, lazy load) and costs nothing when idle. childList only — never
 * attributes — so setting a label here cannot retrigger the observer.
 * Verified against a real DOM: 4 writes per render, 0 after settle, 0 from
 * unrelated page mutations.
 */
(function () {
  'use strict';

  var LABELS = [
    ['a[href="https://judge.me/terms"]',
     "see Judge.me's terms (opens in a new window)"],
    ['a[href="https://judge.me/privacy"]',
     "see Judge.me's privacy policy (opens in a new window)"],
    ['a[href="https://judge.me/content-policy"]',
     "see Judge.me's content policy (opens in a new window)"],
    ['.jdgm-rev__prod-link',
     'see reviews at Judge.me (opens in a new window)']
  ];

  var scheduled = false;

  function apply() {
    scheduled = false;
    for (var i = 0; i < LABELS.length; i++) {
      var els = document.querySelectorAll(LABELS[i][0]);
      for (var j = 0; j < els.length; j++) {
        // Skip the write when nothing would change.
        if (els[j].getAttribute('aria-label') !== LABELS[i][1]) {
          els[j].setAttribute('aria-label', LABELS[i][1]);
        }
      }
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    if (window.requestAnimationFrame) requestAnimationFrame(apply);
    else setTimeout(apply, 16);
  }

  function touchesJudgeMe(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType !== 1) continue;
      var cls = n.className;
      if (typeof cls === 'string' && cls.indexOf('jdgm') !== -1) return true;
      if (n.querySelector && n.querySelector('[class*="jdgm"]')) return true;
    }
    return false;
  }

  function start() {
    apply();                                     // anything already rendered

    if (!window.MutationObserver || !document.body) return;

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (touchesJudgeMe(records[i].addedNodes)) { schedule(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
